import React, { useState, useMemo } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import Stage3PreviewGrid from './Stage3PreviewGrid';

// Sub-component cho tab "Danh sách sản phẩm" — 2 sub-steps internal:
//   • mapping  → ánh xạ cột (giống AntImportFieldMapper)
//   • preview  → xem trước, edit, mark skip (giống AntImportPreviewGrid)
//
// Apply qua API actionApply ở footer cha (Stage3TabbedReview) — KHÔNG gọi
// actionImport, vì luồng này tạo VOUCHER hoàn chỉnh (1 phiếu) từ AI analysis,
// không phải bulk insert rows.

function IconArrowRight() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}
function IconArrowLeft() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}
function IconCheck() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

// Discover các dataField xuất hiện trong items (ít nhất 1 dòng có giá trị).
export function discoverItemFields(items, requiredFields = []) {
    var seen = {};
    var order = requiredFields.slice();
    requiredFields.forEach(function (field) { seen[field] = true; });
    for (var i = 0; i < (items || []).length; i++) {
        var f = (items[i] && items[i].fields) || {};
        for (var k in f) {
            if (!Object.prototype.hasOwnProperty.call(f, k)) continue;
            if (seen[k]) continue;
            var v = f[k];
            if (v == null || v === '') continue;
            seen[k] = true;
            order.push(k);
        }
    }
    return order;
}

function fieldLabel(cnf, field, fallback) {
    if (!cnf) return fallback || field;
    var f = cnf.getCnfField ? cnf.getCnfField(field) : null;
    if (f && f.label) return AntLocalization.translateText(f.label);
    return fallback || field;
}

export default function Stage3ItemsImport(props) {
    var [subStep, setSubStep] = useState('mapping'); // 'mapping' | 'preview'

    var items = props.items || [];
    var itemsCnf = props.config;

    // Mapping state — Item dataField → AI source column (discovered từ items).
    // Khởi tạo: mọi field discovered đã auto-mapped 1-1 (vì AI đã trả về theo
    // dataField canonical từ configAIExtract). User có thể chỉnh mapping hoặc
    // bỏ qua field nào không muốn import.
    var isExpense = props.analysis && props.analysis.target_table === 'EXPOrder';
    var aiColumns = useMemo(function () { return discoverItemFields(items, isExpense ? ['item_code'] : []); }, [items, isExpense]);
    var [mapping, setMapping] = useState(function () {
        var m = {};
        aiColumns.forEach(function (k) { m[k] = k; });
        return m;
    });

    // Apply mapping → transform items (rename keys) trước khi sang preview.
    // Hiện tại 1-1 nên là no-op; sau này khi user remap thực sự thì transform.
    var goPreview = function () { setSubStep('preview'); };
    var goMapping = function () { setSubStep('mapping'); };

    var validCount = items.filter(function (r) {
        return (!r.errors || !r.errors.length) && r.match_status === 'Resolved' && !r.skip_flag;
    }).length;
    var errCount = items.filter(function (r) {
        if (r.skip_flag) return false;
        return (r.errors && r.errors.length) || r.match_status !== 'Resolved';
    }).length;
    var skipCount = items.filter(function (r) { return r.skip_flag; }).length;

    return (
        <div className="aiva-items-flow">
            {/* Sub-stepper */}
            <div className="aiva-substepper">
                <div className={'aiva-substep' + (subStep === 'mapping' ? ' is-active' : '')} onClick={goMapping}>
                    <span className="dot">{subStep === 'preview' ? <IconCheck /> : '1'}</span>
                    <span className="lbl">{AntLocalization.translateText({ vi: 'Ánh xạ cột', en: 'Column mapping', fr: 'Mappage colonnes', es: 'Mapeo columnas', id: 'Pemetaan kolom', ja: '列マッピング', zh: '列映射', ko: '열 매핑', hi: 'कॉलम मैपिंग', ar: 'تعيين الأعمدة' })}</span>
                </div>
                <div className="aiva-substep-sep" />
                <div className={'aiva-substep' + (subStep === 'preview' ? ' is-active' : '')}>
                    <span className="dot">2</span>
                    <span className="lbl">{AntLocalization.translateText({ vi: 'Xem trước & sửa', en: 'Preview & edit', fr: 'Aperçu & édition', es: 'Vista previa', id: 'Pratinjau', ja: 'プレビュー', zh: '预览', ko: '미리보기', hi: 'पूर्वावलोकन', ar: 'معاينة' })}</span>
                </div>
            </div>

            <div className="aiva-substep-body">
                {subStep === 'mapping' && (
                    <ItemsColumnMapper
                        aiColumns={aiColumns}
                        mapping={mapping}
                        onChange={setMapping}
                        cnf={itemsCnf}
                        items={items}
                    />
                )}
                {subStep === 'preview' && (
                    <div className="aiva-preview-pane">
                        <div className="aiva-preview-stats">
                            <span className="chip chip-ok">{validCount} {AntLocalization.translateText({ vi: 'hợp lệ', en: 'valid', fr: 'valides', es: 'válidas', id: 'valid', ja: '有効', zh: '有效', ko: '유효', hi: 'मान्य', ar: 'صالح' })}</span>
                            {errCount > 0 && <span className="chip chip-warn">{errCount} {AntLocalization.translateText({ vi: 'cần xem lại', en: 'to review', fr: 'à revoir', es: 'por revisar', id: 'perlu dicek', ja: '要確認', zh: '待复核', ko: '확인 필요', hi: 'जाँच ज़रूरी', ar: 'للمراجعة' })}</span>}
                            {skipCount > 0 && <span className="chip chip-neutral">{skipCount} {AntLocalization.translateText({ vi: 'bỏ qua', en: 'skipped', fr: 'ignorées', es: 'omitidas', id: 'dilewati', ja: 'スキップ', zh: '已跳过', ko: '건너뜀', hi: 'छोड़े गए', ar: 'متخطاة' })}</span>}
                        </div>
                        <Stage3PreviewGrid
                            items={items}
                            mapping={isExpense ? { ...mapping, item_code: 'item_code' } : mapping}
                            isExpense={isExpense}
                            config={itemsCnf}
                            onChange={props.onChange}
                        />
                    </div>
                )}
            </div>

            <div className="aiva-substep-nav">
                {subStep === 'mapping' ? (
                    <button type="button" className="btn btn-primary" onClick={goPreview}>
                        {AntLocalization.translateText({ vi: 'Xem trước', en: 'Preview', fr: 'Aperçu', es: 'Vista previa', id: 'Pratinjau', ja: 'プレビュー', zh: '预览', ko: '미리보기', hi: 'पूर्वावलोकन', ar: 'معاينة' })}
                        <IconArrowRight />
                    </button>
                ) : (
                    <button type="button" className="btn btn-secondary" onClick={goMapping}>
                        <IconArrowLeft />
                        {AntLocalization.translateText({ vi: 'Quay lại ánh xạ', en: 'Back to mapping', fr: 'Retour au mappage', es: 'Volver al mapeo', id: 'Kembali ke mapping', ja: 'マッピングに戻る', zh: '返回映射', ko: '매핑으로 돌아가기', hi: 'मैपिंग पर लौटें', ar: 'العودة للربط' })}
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── Sub-component: column mapping (Item field ↔ AI source column) ─────
// UI tương tự AntImportFieldMapper nhưng đơn giản hơn: AI đã suggest mapping
// 1-1 (cùng dataField), user chỉ cần xem + bỏ qua field không muốn import.
// Hiển thị thêm SAMPLE values từ items để user biết cột có data thực sự.
function ItemsColumnMapper(props) {
    var aiColumns = props.aiColumns || [];
    var mapping = props.mapping || {};
    var cnf = props.cnf;
    var items = props.items || [];

    // Lấy mẫu giá trị (tối đa 3 distinct) từ rows cho mỗi AI column → user thấy
    // cột này có data gì để quyết định map/skip.
    var samples = useMemo(function () {
        var out = {};
        aiColumns.forEach(function (col) {
            var seen = {};
            var list = [];
            for (var i = 0; i < items.length && list.length < 3; i++) {
                var f = items[i] && items[i].fields;
                if (!f) continue;
                var v = f[col];
                if (v == null || v === '') continue;
                var key = String(v);
                if (seen[key]) continue;
                seen[key] = true;
                list.push(key);
            }
            out[col] = list;
        });
        return out;
    }, [aiColumns, items]);

    var toggleSkip = function (col) {
        var next = Object.assign({}, mapping);
        if (next[col]) delete next[col];
        else next[col] = col;
        if (props.onChange) props.onChange(next);
    };

    return (
        <div className="aiva-mapper">
            <div className="aiva-mapper-head">
                <div className="cell">{AntLocalization.translateText({ vi: 'Trường', en: 'Field', fr: 'Champ', es: 'Campo', id: 'Kolom', ja: '項目', zh: '字段', ko: '필드', hi: 'फ़ील्ड', ar: 'الحقل' })}</div>
                <div className="cell">{AntLocalization.translateText({ vi: 'Mẫu giá trị', en: 'Sample values', fr: 'Exemples', es: 'Ejemplos', id: 'Contoh nilai', ja: 'サンプル値', zh: '示例值', ko: '샘플 값', hi: 'नमूना मान', ar: 'قيم نموذجية' })}</div>
                <div className="cell">{AntLocalization.translateText({ vi: 'Sử dụng', en: 'Use', fr: 'Utiliser', es: 'Usar', id: 'Pakai', ja: '使用', zh: '使用', ko: '사용', hi: 'उपयोग', ar: 'استخدام' })}</div>
            </div>
            {aiColumns.map(function (col) {
                var inUse = !!mapping[col];
                var label = fieldLabel(cnf, col, col);
                var sList = samples[col] || [];
                return (
                    <div key={col} className={'aiva-mapper-row' + (inUse ? '' : ' is-skipped')}>
                        <div className="cell">{label}</div>
                        <div className="cell aiva-samples">
                            {sList.length === 0
                                ? <span className="aiva-sample-empty">—</span>
                                : sList.map(function (s, idx) {
                                    return <span key={idx} className="aiva-sample-chip">{s}</span>;
                                })}
                        </div>
                        <div className="cell">
                            <label className="aiva-toggle">
                                <input type="checkbox" checked={inUse} onChange={function () { toggleSkip(col); }} />
                                <span>{inUse
                                    ? AntLocalization.translateText({ vi: 'Bật', en: 'On', fr: 'Oui', es: 'Sí', id: 'Ya', ja: 'オン', zh: '开', ko: '켬', hi: 'चालू', ar: 'نعم' })
                                    : AntLocalization.translateText({ vi: 'Tắt', en: 'Off', fr: 'Non', es: 'No', id: 'Tidak', ja: 'オフ', zh: '关', ko: '끔', hi: 'बंद', ar: 'لا' })}
                                </span>
                            </label>
                        </div>
                    </div>
                );
            })}
            {aiColumns.length === 0 && (
                <div className="aiva-mapper-empty">
                    {AntLocalization.translateText({ vi: 'Không có cột AI trích xuất được. Quay lại bước Phân tích.', en: 'No AI-extracted columns. Go back to the Analyze step.', fr: 'Aucune colonne extraite par l’IA. Revenez à l’étape Analyse.', es: 'La IA no extrajo columnas. Vuelve al paso Análisis.', id: 'Tidak ada kolom hasil ekstraksi AI. Kembali ke langkah Analisis.', ja: 'AI が抽出した列がありません。解析ステップに戻ってください。', zh: 'AI 未提取到任何列。请返回分析步骤。', ko: 'AI가 추출한 열이 없습니다. 분석 단계로 돌아가세요.', hi: 'AI कोई कॉलम नहीं निकाल सका। विश्लेषण चरण पर लौटें।', ar: 'لم يستخرج الذكاء الاصطناعي أي أعمدة. عُد إلى خطوة التحليل.' })}
                </div>
            )}
        </div>
    );
}
